import React, { useState, useEffect, useMemo } from "react";
import { message } from "antd";
import { HistoryOutlined, ShopOutlined, UserOutlined } from "@ant-design/icons";
import { apiClient } from "../../../shared/api/AllApi";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency } from "../../../utils/formatters";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { useTranslation } from "react-i18next";
import {
  vendorFinanceFromConsignment,
  vendorItemAmount,
  vendorSaleAmount,
} from "../../purchasing/lib/vendorFinance";

const saleAmount = (r) => parseFloat(r.total_price ?? r.total ?? 0) || 0;
const saleOutstanding = (r) => {
  const owed = parseFloat(r.outstanding_balance);
  if (Number.isFinite(owed)) return Math.max(0, owed);
  if (r.payment_method === "credit" && r.status !== "returned" && r.status !== "cancelled") {
    return Math.max(0, saleAmount(r) - (parseFloat(r.amount_paid) || 0));
  }
  return 0;
};

/* ── Client Statement ─────────────────────────────────────────────── */
const ClientStatement = ({ entityId, currency, t }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) {
      setRecords([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient.get("/pos/sales", { params: { customer_id: entityId } })
      .then((r) => setRecords(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        message.error(t("posClientStatementLoadFailed", "Failed to load client statement"));
        setRecords([]);
      })
      .finally(() => setLoading(false));
  }, [entityId, t]);

  const { totalAmount, creditOwed, saleCount } = useMemo(() => {
    const active = records.filter((r) => r.status !== "cancelled");
    return {
      saleCount: active.length,
      totalAmount: active.reduce((s, r) => s + saleAmount(r), 0),
      creditOwed: active.reduce((s, r) => s + saleOutstanding(r), 0),
    };
  }, [records]);

  const statusBadge = (r) => {
    if (r.status === "returned") return <span className="pos-badge pos-badge-danger">{t("posOrdersStatusReturned", "Returned")}</span>;
    if (r.status === "cancelled") return <span className="pos-badge pos-badge-muted">{t("posOrdersStatusCancelled", "Cancelled")}</span>;
    if (r.payment_method === "cash") return <span className="pos-badge pos-badge-success">{t("posOrdersStatusPaidCash", "Paid")}</span>;
    return <span className="pos-badge pos-badge-warning">{t("posOrdersStatusCreditUnpaid", "Credit")}</span>;
  };

  const rowTone = (r) => {
    if (r.status === "returned" || r.status === "cancelled") return "danger";
    if (r.payment_method === "credit" && saleOutstanding(r) > 0) return "warning";
    return "success";
  };

  return (
    <>
      <div className="pos-statement-summary-row">
        <SummaryCard
          tone="accent"
          label={t("posClientStatementSales", "Sales")}
          value={saleCount}
        />
        <SummaryCard
          tone="success"
          label={t("posClientStatementTotal", "Total amount")}
          value={formatCurrency(totalAmount, currency)}
        />
        <SummaryCard
          tone={creditOwed > 0 ? "warning" : "success"}
          label={t("posClientStatementCreditOwed", "Credit owed")}
          value={formatCurrency(creditOwed, currency)}
        />
      </div>
      <div className="pos-sub-list">
        {loading ? (
          <EmptyState title={t("posLoading", "Loading...")} />
        ) : records.length === 0 ? (
          <EmptyState
            icon={<HistoryOutlined />}
            title={t("posClientStatementEmpty", "No sales yet")}
            sub={t("posClientStatementEmptySub", "No sales found for this customer")}
          />
        ) : (
          <div className="pos-orders-list">
            {records.map((r) => (
              <div
                key={r.id}
                className={`pos-orders-row pos-orders-row--${rowTone(r)}`}
                style={{ cursor: "default" }}
              >
                <div className="pos-orders-row-left">
                  <div className="pos-orders-id">
                    {r.document_no || formatOrderNo(r.id, r.created_at)}
                  </div>
                  <div className="pos-orders-meta">
                    <span className="pos-orders-date">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </span>
                    {Number(r.commission_amount) > 0 && !(r.is_taxed === false || r.is_taxed === 0) && (
                      <span className="pos-orders-date" style={{ color: "var(--pos-warning)" }}>
                        +{formatCurrency(r.commission_amount, currency)} {t("posTerminalCommission", "Comm.")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="pos-orders-row-right">
                  <div className="pos-orders-total">{formatCurrency(saleAmount(r), currency)}</div>
                  {saleOutstanding(r) > 0 && (
                    <div className="pos-statement-owed">
                      {t("posClientStatementStillOwes", "Owes")} {formatCurrency(saleOutstanding(r), currency)}
                    </div>
                  )}
                  <div style={{ marginTop: 4 }}>{statusBadge(r)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

/* ── Vendor / Farmer Statement ────────────────────────────────────── */
const VendorStatement = ({ entityId, currency, lang, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient.get(`/pos/vendor-consignment/${entityId}`)
      .then((consRes) => setData(consRes.data))
      .catch(() => {
        message.error(t("posVendorStatementLoadFailed", "Failed to load vendor statement"));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [entityId, t]);

  if (loading) return <div className="pos-sub-list"><EmptyState title={t("posLoading", "Loading...")} /></div>;
  if (!data) return null;

  // Farmer owed = product gross only (commission is company profit, not deducted).
  const finance = vendorFinanceFromConsignment(data);

  return (
    <>
      <div className="pos-statement-summary-row">
        <SummaryCard
          tone="accent"
          label={t("posVendorStatementGross", "Gross sold")}
          value={formatCurrency(finance.gross, currency)}
        />
        <SummaryCard
          tone="success"
          label={t("posVendorStatementOwed", "Owed to farmer")}
          value={formatCurrency(finance.netOwed, currency)}
        />
      </div>

      <div className="pos-sub-list" style={{ paddingTop: 0 }}>
        {(data.sales || []).length === 0 ? (
          <EmptyState
            icon={<HistoryOutlined />}
            title={t("posVendorStatementEmpty", "No consignment sales yet")}
            sub={t("posVendorStatementEmptySub", "No sales recorded for this farmer yet")}
          />
        ) : (
          <div className="pos-orders-list">
            {data.sales.map((s) => {
              const saleOwed = vendorSaleAmount(s);
              return (
              <div key={s.id} className="pos-statement-consignment-row">
                <div className="pos-statement-consignment-head">
                  <div>
                    <span className="pos-orders-id" style={{ fontSize: 13 }}>
                      {s.document_no || formatOrderNo(s.id, s.created_at)}
                    </span>
                    {s.customer && (
                      <span className="pos-statement-party">→ {s.customer.name}</span>
                    )}
                  </div>
                  <span className="pos-orders-date">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                  </span>
                </div>
                <div className="pos-statement-items">
                  {(s.items || []).map((item, idx) => {
                    const name = item.product
                      ? parseLocalizedName(item.product.name, lang, `#${item.product?.id}`)
                      : t("product", "Product");
                    const lineAmt = vendorItemAmount(item);
                    return (
                      <div key={idx} className="pos-statement-item-row">
                        <span>{name} × {item.quantity}</span>
                        <div className="pos-statement-item-vals">
                          <span className="ok">{formatCurrency(lineAmt, currency)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pos-statement-consignment-foot">
                  <span className="mute">{t("vendorGross", "Gross")}: <b>{formatCurrency(saleOwed, currency)}</b></span>
                  <span className="ok">{t("batchNetOwed", "Owed")}: {formatCurrency(saleOwed, currency)}</span>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

/* ── Helpers ──────────────────────────────────────────────────────── */
const SummaryCard = ({ label, value, tone = "accent" }) => (
  <div className={`pos-statement-summary-card pos-statement-summary-card--${tone}`}>
    <div className="pos-statement-summary-label">{label}</div>
    <div className="pos-statement-summary-value">{value}</div>
  </div>
);

const EmptyState = ({ icon, title, sub }) => (
  <div className="pos-sub-empty">
    {icon && <div className="pos-sub-empty-icon">{icon}</div>}
    <div className="pos-sub-empty-title">{title}</div>
    {sub && <div className="pos-sub-empty-sub">{sub}</div>}
  </div>
);

/* ── Root ─────────────────────────────────────────────────────────── */
const PosSubStatement = ({ type, entityId, entityName, onBack }) => {
  const { t, i18n } = useTranslation();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";
  const isClient = type === "client";

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className={`pos-sub-icon${isClient ? "" : " pos-sub-icon--vendor"}`}>
            {isClient ? <UserOutlined /> : <ShopOutlined />}
          </div>
          <div>
            <div className="pos-statement-kicker">
              {isClient
                ? t("posClientStatementTitle", "Client Statement")
                : t("posVendorStatementTitle", "Farmer Statement")}
            </div>
            <h2 className="pos-statement-entity">{entityName || "—"}</h2>
          </div>
        </div>
        <button type="button" className="pos-sub-back" onClick={onBack}>
          {t("posBackToPOS", "Back")}
        </button>
      </div>

      {isClient ? (
        <ClientStatement entityId={entityId} currency={currency} t={t} />
      ) : (
        <VendorStatement entityId={entityId} currency={currency} lang={i18n.language} t={t} />
      )}
    </div>
  );
};

export default PosSubStatement;
